/**
 * Why a turn failed, in words a user can act on. Ported from `bimgo-ai-web`'s
 * `errorHandler`.
 *
 * The point of a taxonomy here rather than one "request failed" line: the
 * failures a real session meets are not interchangeable. Out of credits means
 * top up; context length exceeded means start a new conversation; a model
 * authentication error means tell an administrator. Collapsing them into one
 * sentence leaves the user with nothing to do.
 *
 * **Reading the body is the fiddly part**, and the order below is load-bearing.
 * The three services in front of this all shape errors differently:
 *
 *   - the local agent's `AppError` sends `{ error: { code, message } }`, already
 *     written for a human;
 *   - api-v2's RBAC failures put `"Client Error"` in the top-level `error` and
 *     the useful part — `required: [...]` — inside `message`, so a naive
 *     "read `body.error`" shows the user the words "Client Error";
 *   - FastAPI sends `detail`, as a string or an object.
 *
 * So: an explicit `error.message` wins, then the RBAC `required[]` is turned
 * into a sentence, and only then the flat string fields. Getting this order
 * wrong is not a crash — it is a permission error that reads as a server fault.
 */

export type AskErrorCode =
  | "VALIDATION_ERROR"
  | "UNAUTHORIZED"
  | "FORBIDDEN"
  | "NOT_FOUND"
  | "USAGE_LIMIT_EXCEEDED"
  | "CONTEXT_LENGTH_EXCEEDED"
  | "PLUGIN_UPDATE_REQUIRED"
  | "INSTANCE_BUSY"
  | "THREAD_BUSY"
  | "THREAD_NOT_AVAILABLE"
  | "THREAD_OWNER_MISMATCH"
  | "NO_LOCAL_WORKSPACE"
  | "AGENT_RECURSION_LIMIT"
  | "AGENT_TOOL_ERROR"
  | "AGENT_PARSE_ERROR"
  | "MODEL_AUTHENTICATION_ERROR"
  | "MODEL_NOT_FOUND"
  | "MODEL_RATE_LIMIT"
  | "MODEL_OVERLOADED"
  | "MODEL_CONNECTION_LOST"
  | "MODEL_ABORTED"
  | "UPSTREAM_ERROR"
  | "SERVICE_UNAVAILABLE"
  | "INTERNAL_ERROR";

const KEYS: Record<AskErrorCode, string> = {
  VALIDATION_ERROR: "error.validationError",
  UNAUTHORIZED: "error.unauthorized",
  FORBIDDEN: "error.forbidden",
  NOT_FOUND: "error.notFound",
  USAGE_LIMIT_EXCEEDED: "error.usageLimitExceeded",
  CONTEXT_LENGTH_EXCEEDED: "error.contextLengthExceeded",
  PLUGIN_UPDATE_REQUIRED: "error.pluginUpdateRequired",
  INSTANCE_BUSY: "error.instanceBusy",
  THREAD_BUSY: "error.threadBusy",
  THREAD_NOT_AVAILABLE: "threadStatus.unavailable",
  THREAD_OWNER_MISMATCH: "threadStatus.ownerMismatch",
  NO_LOCAL_WORKSPACE: "threadStatus.noLocalWorkspace",
  AGENT_RECURSION_LIMIT: "error.agentRecursionLimit",
  AGENT_TOOL_ERROR: "error.agentToolError",
  AGENT_PARSE_ERROR: "error.agentParseError",
  MODEL_AUTHENTICATION_ERROR: "error.modelAuthenticationError",
  MODEL_NOT_FOUND: "error.modelNotFound",
  MODEL_RATE_LIMIT: "error.modelRateLimit",
  MODEL_OVERLOADED: "error.modelOverloaded",
  MODEL_CONNECTION_LOST: "error.modelConnectionLost",
  MODEL_ABORTED: "error.modelAborted",
  UPSTREAM_ERROR: "error.upstreamError",
  SERVICE_UNAVAILABLE: "error.serviceUnavailable",
  INTERNAL_ERROR: "error.internalError",
};

/** The locale key for a code; unknown codes get the generic one. */
export function errorKey(code: string): string {
  return KEYS[code as AskErrorCode] ?? "error.unknown";
}

/**
 * The four blocks behind one 402, and the sentence each of them earns.
 *
 * api-v2's credit gate refuses for four distinct reasons and says which in
 * `detail.reason` (`services/billing.py`); the agent goes out of its way to keep
 * it — `errors.ts`, `routes/ask.ts` and `bimgo/api.ts` each say so — because the
 * remedies do not overlap:
 *
 *   - `subscription_required`   → nobody can spend anything; renew with Bimgo.
 *   - `insufficient_credits`    → the org wallet is empty; top it up.
 *   - `member_limit_exceeded`   → *this* member's cap; an admin raises it.
 *   - `org_limit_exceeded`      → the org's own cap; an admin raises it.
 *
 * Until #64 nothing here read it, so all four rendered as one sentence that told
 * the user to "wait until next month" — advice that does nothing for three of
 * them and is actively wrong for a lapsed subscription, which no amount of
 * waiting fixes.
 */
export const BLOCK_REASON_KEYS: Record<string, string> = {
  subscription_required: "error.subscriptionRequired",
  insufficient_credits: "error.insufficientCredits",
  member_limit_exceeded: "error.memberLimitExceeded",
  org_limit_exceeded: "error.orgLimitExceeded",
};

/**
 * The locale key for a refusal, reading `details.reason` when there is one.
 *
 * **Only for `USAGE_LIMIT_EXCEEDED`.** `reason` is the billing gate's word;
 * another service is free to put its own `reason` in `details`, and letting any
 * of them redirect the sentence would eventually put "top up your wallet" on a
 * failure that has nothing to do with credits. An unrecognised reason — a fifth
 * block api-v2 adds later — falls back to the code's own sentence rather than to
 * the key itself, so a new server reason degrades to a vague message, never to a
 * raw `error.somethingNew` on screen.
 */
export function errorKeyFor(code: string, details?: Record<string, unknown>): string {
  if (code === "USAGE_LIMIT_EXCEEDED") {
    const reason = details?.["reason"];
    if (typeof reason === "string") {
      const key = BLOCK_REASON_KEYS[reason];
      if (key) return key;
    }
  }
  // The named sentence only when the agent actually told us who was busy; otherwise
  // the unnamed one, so a missing detail can never leave `{provider}` on screen.
  if (code === "MODEL_OVERLOADED" && !providerName(details)) {
    return "error.modelOverloadedUnknown";
  }
  if (code === "MODEL_CONNECTION_LOST" && !providerName(details)) {
    return "error.modelConnectionLostUnknown";
  }
  // Same rule for the held instance (#193): named only when the agent named it.
  if (code === "INSTANCE_BUSY" && !instanceName(details)) {
    return "error.instanceBusyUnknown";
  }
  // A cut-off workflow run that cannot be picked up where it is asked to
  // (`routes/workflows.ts`): nothing is wrong with the request, so "invalid
  // request" would send the user looking for a mistake they did not make.
  if (code === "VALIDATION_ERROR") {
    const reason = details?.["reason"];
    if (reason === "target_changed") {
      return runDocument(details) ? "error.runTargetChanged" : "error.runTargetChangedUnknown";
    }
    if (reason === "target_unknown") return "error.runTargetUnknown";
  }
  return errorKey(code);
}

/** Vendor ids as people write them; an unknown one is "CAD" rather than an id on screen. */
const HOST_NAMES: Record<string, string> = { revit: "Revit", autocad: "AutoCAD" };

/** The model a refused continue was working in — `details.expected.documentTitle` — or null when the agent named none. */
function runDocument(details: Record<string, unknown> | undefined): string | null {
  const expected = details?.["expected"];
  const title = isRecord(expected) ? expected["documentTitle"] : undefined;
  return typeof title === "string" && title.trim() ? title.trim() : null;
}

/** The sentence's `{document}` and `{host}` for a `target_changed` refusal; none for anything else. */
function runTargetVars(details: Record<string, unknown> | undefined): Record<string, string> {
  if (details?.["reason"] !== "target_changed") return {};
  const vendor = details["vendor"];
  const document = runDocument(details);
  return {
    host: (typeof vendor === "string" ? HOST_NAMES[vendor] : undefined) ?? "CAD",
    ...(document ? { document } : {}),
  };
}

/**
 * Provider ids as people write them.
 *
 * The wire carries `openai` / `anthropic` because that is what the agent's config
 * uses, and neither is how anyone spells them — "openai is busy right now" reads like
 * a typo. An id we do not recognise is passed through rather than dropped: a new
 * provider should read slightly wrong, not erase the sentence.
 */
const PROVIDER_NAMES: Record<string, string> = {
  openai: "OpenAI",
  anthropic: "Anthropic",
};

/** The display name the agent named for this failure, or null when it named none. */
export function providerName(details?: Record<string, unknown>): string | null {
  const raw = details?.["provider"];
  if (typeof raw !== "string" || raw.length === 0) return null;
  return PROVIDER_NAMES[raw.toLowerCase()] ?? raw;
}

export interface ParsedAskError {
  code: AskErrorCode;
  /** The server's own words, when it wrote any worth showing. */
  message: string;
  details: Record<string, unknown> | undefined;
  httpStatus: number;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

export function codeForStatus(status: number): AskErrorCode {
  if (status === 400) return "VALIDATION_ERROR";
  if (status === 401) return "UNAUTHORIZED";
  if (status === 403) return "FORBIDDEN";
  if (status === 404) return "NOT_FOUND";
  if (status === 402) return "USAGE_LIMIT_EXCEEDED";
  if (status === 429) return "MODEL_RATE_LIMIT";
  if (status === 502) return "SERVICE_UNAVAILABLE";
  return "INTERNAL_ERROR";
}

export function parseAskError(
  status: number,
  body: unknown,
  statusText?: string,
): ParsedAskError {
  const b = isRecord(body) ? body : {};
  const error = isRecord(b["error"]) ? b["error"] : {};

  const details =
    (isRecord(error["details"]) ? error["details"] : undefined) ??
    (isRecord(b["details"]) ? b["details"] : undefined) ??
    (isRecord(b["message"]) ? b["message"] : undefined) ??
    (isRecord(b["detail"]) ? b["detail"] : undefined);

  const required =
    details && Array.isArray(details["required"]) ? (details["required"] as string[]) : [];

  let message: string;
  if (typeof error["message"] === "string") {
    // The agent's AppError, already written for a human.
    message = error["message"];
  } else if (required.length > 0) {
    // Must come before the flat fields: api-v2 puts "Client Error" in the
    // top-level `error` and the actionable part in `message.required`.
    message = `You do not have permission to perform this action (requires: ${required.join(", ")}).`;
  } else if (typeof b["message"] === "string") {
    message = b["message"];
  } else if (typeof b["detail"] === "string") {
    message = b["detail"];
  } else if (details && typeof details["message"] === "string") {
    // FastAPI's *object* detail — `{ reason, message }`, which is the shape the
    // billing gate raises. Without this branch a 402 read straight from api-v2
    // (rather than relayed by the agent, which lifts `detail.message` into its
    // own `error.message`) arrives with no words at all, and the card shows the
    // catalogue sentence alone. Must stay below `required[]`, which is a
    // different object detail with its own sentence.
    message = details["message"];
  } else if (typeof b["error"] === "string") {
    message = b["error"];
  } else {
    message = statusText || "";
  }

  const rawCode = error["code"] ?? b["error_code"];
  const code =
    typeof rawCode === "string" && rawCode in KEYS
      ? (rawCode as AskErrorCode)
      : codeForStatus(status);

  return { code, message, details, httpStatus: status };
}

/**
 * What to put on screen.
 *
 * The catalogue sentence is the one that tells the user what to *do*, so it
 * leads. The server's own message is appended only when it adds something —
 * a duplicate, or the bare status text, is noise.
 *
 * `details` is optional and carries the billing gate's `reason` when the turn
 * was refused for money: see `errorKeyFor`. A caller that has it must pass it,
 * or four different blocks come back out as one sentence again.
 */
export function describeAskError(
  parsed: { code: string; message: string; details?: Record<string, unknown> | undefined },
  t: (key: string, vars?: Record<string, string | number>) => string,
): string {
  const provider = providerName(parsed.details);

  /**
   * The provider's failure, in the provider's words, under the provider's name (#317).
   *
   * "服務暫時異常，請稍後再試。 (terminated)" told the user something of ours was
   * broken. The agent names a provider only for a failure that is the provider's —
   * a refusal, an in-stream error, a dropped stream — so when it did, the card says
   * whose it was: "OpenAI: Our servers are currently overloaded…". A dropped
   * connection has no provider sentence to quote (undici's word is "terminated"),
   * so it gets one of ours with the socket's reason after it, for support.
   */
  if (provider && PROVIDER_SIDE_CODES.has(parsed.code)) {
    const said = parsed.message.trim();
    if (parsed.code === "MODEL_CONNECTION_LOST") {
      const sentence = t("error.modelConnectionLost", { provider });
      return said ? `${sentence} (${said})` : sentence;
    }
    if (said) return `${provider}: ${said}`;
  }

  const instance = instanceName(parsed.details);
  const vars = { ...(provider ? { provider } : {}), ...(instance ? { instance } : {}), ...runTargetVars(parsed.details) };
  const sentence = t(
    errorKeyFor(parsed.code, parsed.details),
    Object.keys(vars).length > 0 ? vars : undefined,
  );
  const extra = parsed.message.trim();
  if (!extra || extra === sentence) return sentence;

  /**
   * A code that already explains itself does not need the server's words after it.
   *
   * This is where "An unexpected error occurred. Please try again later. (Our servers
   * are currently overloaded. Please try again later.)" came from (#129): the
   * catalogue sentence carried no information, the provider's did, and appending one
   * to the other said "try again later" twice. Now that a capacity failure has its
   * own sentence naming the provider, the raw provider text adds nothing a user can
   * use — so it is dropped rather than parenthesised.
   *
   * It stays in the error `code` on the wire and in the agent log either way, which
   * is where anyone debugging will look for it.
   */
  if (
    parsed.code === "MODEL_OVERLOADED" ||
    parsed.code === "MODEL_RATE_LIMIT" ||
    // The agent's English for these says the same thing the catalogue does.
    parsed.code === "INSTANCE_BUSY" ||
    parsed.code === "THREAD_BUSY" ||
    // And the agent's English for a run that cannot be picked up here.
    parsed.details?.["reason"] === "target_changed" ||
    parsed.details?.["reason"] === "target_unknown"
  ) {
    return sentence;
  }

  return `${sentence} (${extra})`;
}

/** Codes the agent sends for a failure on the model provider's side, never ours. */
const PROVIDER_SIDE_CODES = new Set<string>([
  "UPSTREAM_ERROR",
  "MODEL_OVERLOADED",
  "MODEL_RATE_LIMIT",
  "MODEL_CONNECTION_LOST",
]);

/** The held instance an `INSTANCE_BUSY` refusal names — "Revit 2025 (Tower.rvt)". */
function instanceName(details: Record<string, unknown> | undefined): string | null {
  const instance = details?.["instance"];
  return typeof instance === "string" && instance.trim() ? instance.trim() : null;
}
